import React, { useImperativeHandle, useRef } from 'react';
import { StyleSheet, View, StyleProp, ViewStyle } from 'react-native';
import { MB_ButtonProps } from '@/components/general/button';
import { COLORS } from './constants';
import { MB_ModalRef, MB_Modal } from './MB_Modal';
import { MB_ModalUtilCloseButton } from './MB_ModalUtilCloseButton';
import { MB_ModalAnimationProps, MB_ModalChildren } from './typesAndInterfaces';

interface MB_ModalMobileBottomRef {
  getRef: () => React.RefObject<MB_ModalRef | null>;
}

interface MB_ModalMobileFakeScreenProps extends MB_ModalAnimationProps {
  /**
   * Modal reference object.
   */
  MB_Ref?: React.RefObject<MB_ModalMobileBottomRef>;

  /**
   * Modal visibility.
   */
  isVisible: boolean;

  /**
   * The modal's children.
   */
  children?: MB_ModalChildren;

  /**
   * Style for anything inside the modal.
   */
  container?: StyleProp<ViewStyle>;

  /**
   * Callback called when the modal is dismissed. This should be the default way of listening for
   * modal dismiss since it is called only when modal hide animation is finished.
   */
  onDismiss?: (isVisible: false) => void;

  /**
   * Props to pass to the close button.
   */
  closeButtonProps?: MB_ButtonProps;

  /**
   * Header to show at the top of the modal.
   */
  header?: React.JSX.Element;

  /**
   * Title that is shown.
   */
  title?: string;

  /**
   * Style for the view that wraps the modal's children.
   */
  childrenWrapper?: StyleProp<ViewStyle>;

  /**
   * When set to true, shows the close button.
   */
  showCloseButton?: boolean;

  /**
   * You can pass a custom close button component instead of using the default one.
   */
  closeButton?: React.JSX.Element;

  keyboardAwareProps?: {
    /**
     * Adds an extra offset to the keyboard. Useful if you want to stick elements
     * above the keyboard.
     */
    extraScrollHeight?: number;
  };

  /**
   * Determines when the keyboard should stay visible after a tap.
   *
   *  - 'never' (default), tapping outside of the focused text input when the keyboard
   * is up dismisses the keyboard. When this happens, children won't receive the tap.
   *  - 'always', the keyboard will not dismiss automatically, and the scroll view will
   * not catch taps, but children of the scroll view can catch taps.
   *  - 'handled', the keyboard will not dismiss automatically when the tap was handled
   * by a children, (or captured by an ancestor).
   *  - false, deprecated, use 'never' instead.
   *  - true, deprecated, use 'always' instead.
   */
  keyboardShouldPersistTaps?: boolean | 'always' | 'never' | 'handled' | undefined;
}

const MB_ModalMobileFakeScreen = ({ ...props }: MB_ModalMobileFakeScreenProps) => {
  const modalRef = useRef<MB_ModalRef>(null);

  useImperativeHandle(props.MB_Ref, () => ({
    getRef: () => {
      return modalRef;
    },
  }));

  return (
    <MB_Modal
      MB_Ref={modalRef}
      isVisible={props.isVisible}
      onDismiss={props.onDismiss}
      hideCloseButton
      closeOnBackdropPress
      animationIn={props.animationIn}
      animationInTiming={props.animationInTiming}
      animationOut={props.animationOut}
      animationOutTiming={props.animationOutTiming}
      simplified
      keyboardAwareProps={props.keyboardAwareProps}
      keyboardShouldPersistTaps={props.keyboardShouldPersistTaps}
    >
      <View style={[styles.container, props.container]}>
        {props.header}
        {props.showCloseButton && (
          <>{props.closeButton ? props.closeButton : <MB_ModalUtilCloseButton onPressed={() => props.onDismiss?.(false)} closeButtonProps={props.closeButtonProps} />}</>
        )}
        <View style={[styles.childrenWrapper, props.childrenWrapper]}>{props.children}</View>
      </View>
    </MB_Modal>
  );
};

export { MB_ModalMobileFakeScreen };
export type { MB_ModalMobileBottomRef, MB_ModalMobileFakeScreenProps };

const styles = StyleSheet.create({
  childrenWrapper: {
    alignSelf: 'stretch',
    flex: 1,
  },
  container: {
    backgroundColor: COLORS.darkBackground,
    width: '100%',
    height: '100%',
    alignItems: 'center',
    justifyContent: 'flex-start',
  },
});
